import React, { useState, useEffect } from "react";
import "./ManageUsers.css";
import { BASE_URL } from "./Config";
import { useNavigate } from "react-router-dom";
import { FiTrash2, FiMenu } from 'react-icons/fi';
import { isSuperAdmin } from "../utils/auth";
import AlertCard from "./AlertCard";
import logoImage from "../resources/1000028180.jpeg";

export default function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const menuBtnStyle = {
    background: 'none',
    border: 'solid 1px #f1f1f1',
    textAlign: 'left',
    padding: '12px 20px',
    fontSize: '1rem',
    cursor: 'pointer',
    width: '100%',
    transition: 'background 0.18s',
    borderBottom: '1px solid #f1f1f1',
    color: 'gray',
  };

  const [alertCard, setAlertCard] = useState({ show: false, message: '', type: 'error', title: '' });

  function logout() {
    navigate('/logout');
  }

  // Download Users CSV function
  const downloadUsersCSV = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      if (!token) {
        alert("No authentication token found");
        return;
      }

      console.log("Starting download users CSV...");
      console.log("Token:", token ? "Present" : "Missing");
      console.log("API URL:", `${BASE_URL}/api/admin/download_users_csv`);

      const response = await fetch(`${BASE_URL}/api/admin/download_users_csv`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Response error text:", errorText);
        throw new Error(
          `Failed to download CSV file: ${response.status} ${response.statusText}`
        );
      }

      console.log("Response OK, getting blob...");
      const blob = await response.blob();
      console.log("Blob size:", blob.size);
      console.log("Blob type:", blob.type);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "users_data.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      console.log("Download completed successfully");
    } catch (error) {
      console.error("Download users CSV error:", error);
      alert("Error downloading CSV: " + error.message);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch all users from backend
  function fetchUsers() {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("admin_token");
    fetch(`${BASE_URL}/api/admin/users_list`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setUsers(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setUsers([]);
        setLoading(false);
      });
  }

  // Handle delete user
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BASE_URL}/api/admin/delete_user/${userToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete user');
      setShowDeleteModal(false);
      setUserToDelete(null);
      fetchUsers(); // Refresh the list
    } catch (err) {
      setAlertCard({ show: true, message: 'Error deleting user: ' + err.message, type: 'error', title: 'Error' });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="manage-users-bg">
      {/* Header */}
      <div className="org-header" style={{position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000}}>
        <div className="header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div className="header-logo-container">
            <img
              src={logoImage}
              alt="Power Mind Metrics Logo"
              style={{ height: "90px", width: "100px", marginRight: "10px" }}
            />
            <h1 className="header-logo">Power Mind Metrics</h1>
          </div>
          <nav className="header-nav" style={{ marginLeft: "auto" }}>
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className="menu-button"
              aria-label="Open menu"
            >
              <FiMenu size={24} />
            </button>
          </nav>
        </div>
        {menuOpen && (
          <div className="menu-dropdown">
            {isSuperAdmin() ? (
              <>
                <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/AdminDashboard'); }}>Admin Dashboard</button>
                <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/admin/sub-admins'); }}>Manage Subadmin</button>
                <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/manage-users'); }}>Manage Users</button>
                <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/organizations'); }}>Manage Organization</button>
              </>
            ) : null}

            <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/graph-report'); }}>Graph Report</button>
            <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); logout(); }}>Logout</button>
          </div>
        )}
      </div>

      <div style={{marginTop: "84px"}}></div>

      {/* Users Management Card */}
      <div className="manage-users-card">
        <div className="manage-users-header-row">
          <div className="manage-users-title" style={{ display: 'flex', flexDirection: 'column', marginRight: '0.5rem' }}>
            <span style={{ whiteSpace: 'nowrap' }}>User</span>
            <span style={{ fontWeight: 500 }}>Management</span>
          </div>
          <button
            className="download-csv-btn"
            onClick={downloadUsersCSV}
            style={{
              background: '#11998e',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 24px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background 0.2s',
              height: '40px',
              minWidth: '160px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Download CSV
          </button>
        </div>

        {error && (
          <div className="manage-users-error">
            Error: {error}
          </div>
        )}

        {loading ? (
          <div className="manage-users-loading">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : (
          <div className="manage-users-table-wrapper">
            <table className="manage-users-table">
              <thead>
                <tr>
                  <th>Index <span className="sort-icon"></span></th>
                  <th>Full Name <span className="sort-icon"></span></th>
                  <th>Username <span className="sort-icon"></span></th>
                  <th>Date of Birth <span className="sort-icon"></span></th>
                  <th>Gender <span className="sort-icon"></span></th>
                  <th>Organization <span className="sort-icon"></span></th>
                  <th>Sport <span className="sort-icon"></span></th>
                  <th>Created At <span className="sort-icon"></span></th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((user, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{user.full_name}</td>
                      <td>{user.username}</td>
                      <td>{user.dob}</td>
                      <td>{user.gender}</td>
                      <td>{user.organization}</td>
                      <td>{user.sport}</td>
                      <td>{user.created_at}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button 
                          className="icon-btn" 
                          title="Delete" 
                          onClick={() => handleDeleteClick(user)}
                        >
                          <FiTrash2 size={20} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', color: '#888' }}>No users found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="manage-users-modal-overlay">
          <div className="manage-users-modal" style={{ maxWidth: 400, height: 'auto', gap: 24 }}>
            <h3 className="manage-users-modal-title" style={{ color: '#b91c1c' }}>Delete User</h3>
            <div>
              Are you sure you want to <b>delete {userToDelete?.full_name}?</b>
            </div>
            <div className="manage-users-modal-actions" style={{display: 'flex', justifyContent: 'space-between', gap: '10px'}}>
              <button
                type="button"
                className="manage-users-modal-cancel"
                onClick={() => { setShowDeleteModal(false); setUserToDelete(null); }}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="manage-users-modal-submit"
                onClick={confirmDelete}
                disabled={actionLoading}
              >
                {actionLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        padding: '12px 0',
        textAlign: 'center',
        fontFamily: 'Poppins, sans-serif',
        zIndex: 100
      }}>
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: 'rgba(75, 75, 75, 0.8)',
          fontWeight: 400
        }}>
          © 2025 All rights reserved Power Mind Metrics
        </p>
      </div>
      
      <AlertCard show={alertCard.show} message={alertCard.message} type={alertCard.type} title={alertCard.title} onClose={() => setAlertCard({ ...alertCard, show: false })} />
    </div>
  );
}
